import React, { MouseEvent, useState, useEffect } from 'react';
import { Card, Flex, Button, Select, TextField, Text } from '@radix-ui/themes';

import {
  BlockFingerprint,
  FingerprintValue,
  ConditionOperator,
  FingerprintValueType,
  SlotDataType,
} from '../../types';

import { MonetaryValueInput } from '@/components';
import {
  getOperationsForValueType,
  filterNullOperations,
  formatOperator,
} from './operationsByValueType';

// Block field enum
export enum BlockField {
  Hash = 'HASH',
  Number = 'NUMBER',
  Timestamp = 'TIMESTAMP',
  TransactionCount = 'TRANSACTION_COUNT',
  ValueEth = 'VALUE_ETH',
  ValueUsd = 'VALUE_USD',
  AverageValueEth = 'AVERAGE_VALUE_ETH',
  AverageValueUsd = 'AVERAGE_VALUE_USD',
  EthPriceUsd = 'ETH_PRICE_USD',
}

// Block field attributes with their types
// All block fields are non-nullable - blocks always have these properties
export const blockAttributes: {
  key: BlockField;
  label: string;
  type: SlotDataType;
  valueType: FingerprintValueType;
  nullable: boolean;
}[] = [
  {
    key: BlockField.Number,
    label: 'Block Number',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.Number,
    nullable: false,
  },
  {
    key: BlockField.ValueEth,
    label: 'Total Value (ETH)',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.MonetaryValue,
    nullable: false,
  },
  {
    key: BlockField.ValueUsd,
    label: 'Total Value (USD)',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.MonetaryValue,
    nullable: false,
  },
  {
    key: BlockField.AverageValueEth,
    label: 'Avg Tx Value (ETH)',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.MonetaryValue,
    nullable: false,
  },
  {
    key: BlockField.AverageValueUsd,
    label: 'Avg Tx Value (USD)',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.MonetaryValue,
    nullable: false,
  },
  {
    key: BlockField.TransactionCount,
    label: 'Transaction Count',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.Number,
    nullable: false,
  },
  {
    key: BlockField.EthPriceUsd,
    label: 'ETH Price (USD)',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.Number,
    nullable: false,
  },
  {
    key: BlockField.Timestamp,
    label: 'Timestamp',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.Number,
    nullable: false,
  },
  {
    key: BlockField.Hash,
    label: 'Hash',
    type: SlotDataType.String,
    valueType: FingerprintValueType.Hash,
    nullable: false,
  },
];

const TextFieldInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  return (
    <TextField.Root value={value} onChange={(e) => onChange(e.target.value)} />
  );
};

// Range input for Between/NotBetween with regular numbers
const NumberRangeInput = ({
  value,
  onChange,
}: {
  value: { min: string; max: string };
  onChange: (value: { min: string; max: string }) => void;
}) => {
  const rangeValue = value || { min: '', max: '' };
  return (
    <Flex direction="row" gap="2" align="center">
      <TextField.Root
        type="number"
        placeholder="Min"
        value={rangeValue.min}
        onChange={(e) => onChange({ ...rangeValue, min: e.target.value })}
        style={{ flex: 1 }}
      />
      <Text size="2" color="gray">
        and
      </Text>
      <TextField.Root
        type="number"
        placeholder="Max"
        value={rangeValue.max}
        onChange={(e) => onChange({ ...rangeValue, max: e.target.value })}
        style={{ flex: 1 }}
      />
    </Flex>
  );
};

// Range input for Between/NotBetween with monetary values
const MonetaryRangeInput = ({
  value,
  onChange,
  defaultCurrency = 'ETH',
}: {
  value: {
    min: { amount: number | string; currency: string };
    max: { amount: number | string; currency: string };
  };
  onChange: (value: {
    min: { amount: number | string; currency: string };
    max: { amount: number | string; currency: string };
  }) => void;
  defaultCurrency?: string;
}) => {
  const rangeValue = value || {
    min: { amount: '', currency: defaultCurrency },
    max: { amount: '', currency: defaultCurrency },
  };
  return (
    <Flex direction="column" gap="2">
      <Flex direction="row" gap="2" align="center">
        <Text size="2" color="gray" style={{ minWidth: '30px' }}>
          Min
        </Text>
        <MonetaryValueInput
          value={rangeValue.min}
          onChange={(minValue) => onChange({ ...rangeValue, min: minValue })}
        />
      </Flex>
      <Flex direction="row" gap="2" align="center">
        <Text size="2" color="gray" style={{ minWidth: '30px' }}>
          Max
        </Text>
        <MonetaryValueInput
          value={rangeValue.max}
          onChange={(maxValue) => onChange({ ...rangeValue, max: maxValue })}
        />
      </Flex>
    </Flex>
  );
};

// Check if operation is a range operation
const isRangeOperation = (op: ConditionOperator): boolean =>
  op === ConditionOperator.Between || op === ConditionOperator.NotBetween;

const fingerprintValueComponentMap: Record<
  FingerprintValueType,
  React.ComponentType<any>
> = {
  [FingerprintValueType.MonetaryValue]: MonetaryValueInput,
  [FingerprintValueType.String]: TextFieldInput,
  [FingerprintValueType.Boolean]: TextFieldInput,
  [FingerprintValueType.Hash]: TextFieldInput,
  [FingerprintValueType.Null]: TextFieldInput,
  [FingerprintValueType.Number]: TextFieldInput,
};

export interface BlockFingerprintEditorProps {
  initialFingerprint?: Partial<BlockFingerprint>;
  onSave: (fingerprint: Partial<BlockFingerprint>) => void;
  onCancel: () => void;
}

const BlockFingerprintEditor = ({
  initialFingerprint,
  onSave,
  onCancel,
}: BlockFingerprintEditorProps) => {
  const [selectedAttribute, setSelectedAttribute] = useState(
    initialFingerprint?.field || blockAttributes[0].key,
  );
  const [operation, setOperation] = useState<ConditionOperator>(
    initialFingerprint?.operation || ConditionOperator.Equals,
  );
  const [value, setValue] = useState<FingerprintValue | undefined>(
    initialFingerprint?.value,
  );

  // Get the valueType for the selected attribute
  const selectedAttr = blockAttributes.find(
    (attr) => attr.key === selectedAttribute,
  );
  const expectedValueType =
    selectedAttr?.valueType || FingerprintValueType.String;
  const isNullable = selectedAttr?.nullable ?? false;

  // Get valid operations for this value type, filtering null ops if not nullable
  const validOperations = filterNullOperations(
    getOperationsForValueType(expectedValueType),
    isNullable,
  );

  // Get default currency for monetary fields
  const getDefaultCurrency = () =>
    selectedAttribute === BlockField.ValueUsd ||
    selectedAttribute === BlockField.AverageValueUsd ||
    selectedAttribute === BlockField.EthPriceUsd
      ? 'USD'
      : 'ETH';

  // Helper to get initial value based on type and operation
  const getInitialValue = (
    valueType: FingerprintValueType,
    isRange: boolean,
  ) => {
    const currency = getDefaultCurrency();
    if (valueType === FingerprintValueType.MonetaryValue) {
      if (isRange) {
        return {
          min: { amount: '', currency },
          max: { amount: '', currency },
        };
      }
      return { amount: '', currency };
    }
    if (isRange) {
      return { min: '', max: '' };
    }
    return '';
  };

  // Update value's valueType when selected attribute changes
  useEffect(() => {
    setValue((currentValue: FingerprintValue | undefined) => {
      if (!currentValue || currentValue.valueType !== expectedValueType) {
        return {
          value: getInitialValue(
            expectedValueType,
            isRangeOperation(operation),
          ),
          valueType: expectedValueType,
        };
      }
      return currentValue;
    });

    // Reset operation if current operation is not valid for the new value type
    setOperation((currentOperation: ConditionOperator) => {
      if (!validOperations.includes(currentOperation)) {
        return validOperations[0];
      }
      return currentOperation;
    });
  }, [selectedAttribute, expectedValueType, validOperations]);

  // Reset value when switching between range and non-range operations
  useEffect(() => {
    setValue((currentValue: FingerprintValue | undefined) => {
      const needsRange = isRangeOperation(operation);
      const hasRange =
        currentValue?.value &&
        typeof currentValue.value === 'object' &&
        'min' in currentValue.value;

      if (needsRange !== hasRange) {
        return {
          value: getInitialValue(expectedValueType, needsRange),
          valueType: expectedValueType,
        };
      }
      return currentValue;
    });
  }, [operation, expectedValueType]);

  const handleSaveClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    const fingerprint: Partial<BlockFingerprint> = {
      field: selectedAttribute,
      operation,
      value,
    };
    onSave(fingerprint);
  };

  const ValueFieldComponent = fingerprintValueComponentMap[expectedValueType];

  return (
    <Card size="1">
      <Flex direction="column" gap="2">
        <Select.Root
          value={selectedAttribute}
          onValueChange={(val) =>
            setSelectedAttribute(val as typeof selectedAttribute)
          }
        >
          <Select.Trigger />
          <Select.Content>
            {blockAttributes.map((attr) => (
              <Select.Item key={attr.key} value={attr.key}>
                {attr.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>

        <Select.Root
          value={operation}
          onValueChange={(value) => setOperation(value as ConditionOperator)}
        >
          <Select.Trigger />
          <Select.Content>
            {validOperations.map((op: ConditionOperator) => (
              <Select.Item key={op} value={op}>
                {formatOperator(op)}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        {isRangeOperation(operation) ? (
          expectedValueType === FingerprintValueType.MonetaryValue ? (
            <MonetaryRangeInput
              value={value?.value}
              onChange={(newValue) => {
                setValue({
                  value: newValue,
                  valueType: expectedValueType,
                });
              }}
              defaultCurrency={getDefaultCurrency()}
            />
          ) : (
            <NumberRangeInput
              value={value?.value}
              onChange={(newValue) => {
                setValue({
                  value: newValue,
                  valueType: expectedValueType,
                });
              }}
            />
          )
        ) : (
          <ValueFieldComponent
            value={value?.value}
            onChange={(newValue: FingerprintValue['value']) => {
              setValue({
                value: newValue,
                valueType: expectedValueType,
              });
            }}
          />
        )}
        <Flex gap="2" justify="between">
          <Button onClick={onCancel} variant="soft">
            Cancel
          </Button>
          <Button onClick={handleSaveClick}>
            {initialFingerprint ? 'Update Condition' : 'Save Condition'}
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
};

export default BlockFingerprintEditor;
